import { Button } from "@/components/common/Button/Button"
import { ModalBase } from "@/components/common/ModalBase/ModalBase"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import {
  SHAREPOINT_CONFIG,
  isValidSharepointExtension,
} from "@/config/sharepoint"
import type { SharepointBrowseHook } from "@/hooks/useSharepointBrowse"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useGetSyncStatus } from "@/services/api/sharepointConnectionService"
import { KnowledgebaseFile } from "@/types/knowledgebase"
import {
  SharepointBrowseItem,
  SharepointFolderSelection,
  SharepointSite,
  SharepointSyncStatusResponse,
} from "@/types/sharepointConnection"
import { getFileExtension } from "@/utils/fileUtils"
import { getSharepointFiles } from "@/utils/sharepointUtils"
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCloud,
  IconFile,
  IconLoader2,
} from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { SharepointFolderBrowser } from "./SharepointFolderBrowser"

interface SharepointFolderPickerProps {
  onClose: () => void
  onStartSync: (
    accessToken: string,
    folders: SharepointFolderSelection[],
  ) => Promise<string>
  onSyncComplete?: () => void
  onDisconnect?: () => void
  onUnlinkAll?: () => void
  importedFiles?: KnowledgebaseFile[]
  connectionId?: string
  sharepointBrowse: SharepointBrowseHook
}

interface SelectedItem {
  item: SharepointBrowseItem
  site: SharepointSite
  path: string
  driveId: string
}

function isValidFileType(name: string): boolean {
  const ext = getFileExtension(name)
  return isValidSharepointExtension(ext)
}

export const SharepointFolderPicker: React.FC<SharepointFolderPickerProps> = ({
  onClose,
  onStartSync,
  onSyncComplete,
  onDisconnect,
  onUnlinkAll,
  importedFiles = [],
  connectionId,
  sharepointBrowse,
}) => {
  const { translate } = useTranslation()
  const {
    sites,
    currentItems,
    loading,
    error,
    getAccessToken,
    graphFetch,
    loadSites,
    browseFolderContents,
  } = sharepointBrowse

  const [selectedSite, setSelectedSite] = useState<SharepointSite | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([])
  const [currentPathParts, setCurrentPathParts] = useState<string[]>([])
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(
    new Map(),
  )
  const [currentDriveId, setCurrentDriveId] = useState<string>("")
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] =
    useState<SharepointSyncStatusResponse | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [importedFilesExpanded, setImportedFilesExpanded] = useState(false)

  // Track recursive file counts for folders
  const [folderCounts, setFolderCounts] = useState<
    Map<string, { count: number; loading: boolean }>
  >(new Map())

  // Single sync status hook — polls while syncing, one-shot on mount via refetch
  const { data: syncStatus, refetch: refetchSyncStatus } = useGetSyncStatus(
    connectionId,
    syncing,
  )

  // On mount, check if there's an active sync for this connection
  useEffect(() => {
    if (connectionId) refetchSyncStatus()
  }, [connectionId, refetchSyncStatus])

  // Detect active sync from fetched data
  useEffect(() => {
    if (syncStatus?.status === "syncing" && !syncing) setSyncing(true)
  }, [syncStatus, syncing])

  // Handle sync completion
  useEffect(() => {
    if (!syncStatus || !syncing) return
    if (syncStatus.status === "completed" || syncStatus.status === "failed") {
      setSyncResult(syncStatus)
      setSyncing(false)
      onSyncComplete?.()
    }
  }, [syncStatus, syncing, onSyncComplete])

  useEffect(() => {
    loadSites()
  }, [loadSites])

  const handleSiteSelect = useCallback(
    async (siteId: string) => {
      const site = sites.find((s) => s.id === siteId)
      if (!site) return

      setSelectedSite(site)
      setBreadcrumbs([])
      setCurrentPathParts([])

      try {
        const drive = await graphFetch(`/sites/${site.id}/drive`)
        setCurrentDriveId(drive.id || "")
      } catch {
        setCurrentDriveId("")
      }

      await browseFolderContents(site.id, "/")
    },
    [sites, browseFolderContents, graphFetch],
  )

  const handleFolderClick = useCallback(
    async (folder: SharepointBrowseItem) => {
      if (!selectedSite) return
      const newParts = [...currentPathParts, folder.name]
      const newPath = "/" + newParts.join("/")
      setCurrentPathParts(newParts)
      setBreadcrumbs(newParts)
      await browseFolderContents(selectedSite.id, newPath)
    },
    [selectedSite, currentPathParts, browseFolderContents],
  )

  const handleBreadcrumbClick = useCallback(
    async (index: number) => {
      if (!selectedSite) return
      if (index === -1) {
        setCurrentPathParts([])
        setBreadcrumbs([])
        await browseFolderContents(selectedSite.id, "/")
      } else {
        const newParts = currentPathParts.slice(0, index + 1)
        const newPath = "/" + newParts.join("/")
        setCurrentPathParts(newParts)
        setBreadcrumbs(newParts)
        await browseFolderContents(selectedSite.id, newPath)
      }
    },
    [selectedSite, currentPathParts, browseFolderContents],
  )

  // Fetch recursive file count for a folder using Delta API
  const fetchFolderCount = useCallback(
    async (folderId: string, driveId: string): Promise<number> => {
      try {
        interface GraphItem {
          folder?: unknown
          name?: string
        }
        let allItems: GraphItem[] = []
        let nextEndpoint: string | null =
          `/drives/${driveId}/items/${folderId}/delta`

        // Follow pagination to get all items
        while (nextEndpoint) {
          const data: { value?: GraphItem[]; "@odata.nextLink"?: string } =
            await graphFetch(nextEndpoint)

          allItems = allItems.concat(data.value || [])

          // Extract endpoint from full URL for next page
          if (data["@odata.nextLink"]) {
            const url = new URL(data["@odata.nextLink"])
            const pathWithQuery = url.pathname + url.search
            // Microsoft Graph pathname includes `/v1.0/...`, but graphFetch already
            // prefixes `https://graph.microsoft.com/v1.0`, so strip leading `/v1.0`
            // to avoid `/v1.0/v1.0/...` duplication
            const v1Prefix = "/v1.0"
            nextEndpoint = pathWithQuery.startsWith(v1Prefix)
              ? pathWithQuery.slice(v1Prefix.length)
              : pathWithQuery
          } else {
            nextEndpoint = null
          }
        }

        // Count only valid files (not folders)
        const validFileCount = allItems.filter(
          (item) => !item.folder && item.name && isValidFileType(item.name),
        ).length

        return validFileCount
      } catch (error) {
        console.error("Failed to fetch folder count:", error)
        return 0
      }
    },
    [graphFetch],
  )

  const handleItemToggle = useCallback(
    async (item: SharepointBrowseItem) => {
      if (!selectedSite) return

      const isCurrentlySelected = selectedItems.has(item.id)

      setSelectedItems((prev) => {
        const next = new Map(prev)
        if (next.has(item.id)) {
          next.delete(item.id)
        } else {
          const path = "/" + [...currentPathParts, item.name].join("/")
          next.set(item.id, {
            item,
            site: selectedSite,
            path,
            driveId: currentDriveId,
          })
        }
        return next
      })

      // If selecting a folder, fetch its recursive count
      if (!isCurrentlySelected && item.isFolder) {
        // Mark as loading
        setFolderCounts((prev) =>
          new Map(prev).set(item.id, { count: 0, loading: true }),
        )

        // Fetch count
        const count = await fetchFolderCount(item.id, currentDriveId)

        // Update with actual count
        setFolderCounts((prev) =>
          new Map(prev).set(item.id, { count, loading: false }),
        )
      }
    },
    [
      selectedSite,
      currentPathParts,
      currentDriveId,
      selectedItems,
      fetchFolderCount,
    ],
  )

  const handleClearAll = useCallback(() => {
    setSelectedItems(new Map())
  }, [])

  const handleImport = useCallback(async () => {
    if (selectedItems.size === 0) return

    try {
      setSyncing(true)
      setSyncResult(null)
      setSyncError(null)

      const token = await getAccessToken()

      const folders: SharepointFolderSelection[] = Array.from(
        selectedItems.values(),
      )
        .filter((sel) => sel.item.isFolder || isValidFileType(sel.item.name))
        .map((sel) => ({
          siteId: sel.site.id,
          siteName: sel.site.name,
          driveId: sel.driveId,
          folderId: sel.item.id,
          folderPath: sel.path,
        }))

      await onStartSync(token, folders)
      setSelectedItems(new Map())

      toast.success(translate("SHAREPOINT/IMPORT_STARTED_TOAST"))
    } catch (err) {
      console.error("Failed to import:", err)
      setSyncError(
        err instanceof Error
          ? err.message
          : translate("SHAREPOINT/IMPORT_ERROR_TOAST"),
      )
      setSyncing(false)
    }
  }, [selectedItems, getAccessToken, onStartSync, translate])

  const selectedItemIds = useMemo(
    () => new Set(selectedItems.keys()),
    [selectedItems],
  )
  const sharepointImportedFiles = useMemo(
    () => getSharepointFiles(importedFiles),
    [importedFiles],
  )
  const importedItemNames = useMemo(
    () => new Set(sharepointImportedFiles.map((f) => f.fileName)),
    [sharepointImportedFiles],
  )

  const totalEstimatedFiles = useMemo(() => {
    let count = 0
    for (const sel of selectedItems.values()) {
      if (sel.item.isFolder) {
        // For folders, only use accurate recursive count (not childCount)
        const folderCount = folderCounts.get(sel.item.id)
        if (folderCount && !folderCount.loading) {
          count += folderCount.count
        }
        // If still loading, don't add to count (will show "counting..." instead)
      } else {
        // Only count individual valid files
        if (isValidFileType(sel.item.name)) {
          count += 1
        }
      }
    }
    return count
  }, [selectedItems, folderCounts])

  const isCountingFolders = useMemo(() => {
    return Array.from(selectedItems.values()).some((sel) => {
      const folderCount = folderCounts.get(sel.item.id)
      return sel.item.isFolder && folderCount?.loading
    })
  }, [selectedItems, folderCounts])

  const exceedsFileLimit =
    totalEstimatedFiles > SHAREPOINT_CONFIG.MAX_SYNC_FILES

  return (
    <ModalBase onClose={onClose} showSidebarLeft>
      <div
        className="mx-auto flex h-full flex-col"
        style={{ maxWidth: 640, width: "100%" }}
      >
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yellow1/10">
            <IconCloud size={18} className="text-yellow1" />
          </div>
          <div className="flex-1">
            <Typography variant="textS" as="h2" className="font-semibold">
              {translate("SHAREPOINT/PICKER_TITLE")}
            </Typography>
            <Typography variant="textXS" as="p" className="text-grey5">
              {translate("SHAREPOINT/PICKER_DESCRIPTION")}
            </Typography>
          </div>
          {onDisconnect && (
            <button
              onClick={onDisconnect}
              className="shrink-0 rounded border border-grey3 px-2.5 py-1 text-xs text-grey5 transition-colors hover:border-red1/50 hover:text-red1"
            >
              {translate("SHAREPOINT/DISCONNECT_BUTTON")}
            </button>
          )}
        </div>

        {/* Error */}
        {(error || syncError) && (
          <div className="mb-3 rounded-lg bg-red1/10 px-3 py-2 text-sm text-red1">
            {error || syncError}
          </div>
        )}

        {/* Sync Result Banner */}
        {syncResult && !syncing && (
          <div className="mb-3 rounded-lg border border-yellow1/20 bg-yellow1/5 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <IconCheck size={16} className="text-yellow1" />
              <span className="text-sm font-medium text-yellow1">
                {syncResult.status === "completed"
                  ? translate("SHAREPOINT/IMPORT_COMPLETED")
                  : translate("SHAREPOINT/IMPORT_FAILED")}
              </span>
            </div>
            <div className="mt-1 pl-6 text-xs text-grey5">
              {syncResult.filesAdded === 0 &&
              syncResult.filesSkipped === 0 &&
              syncResult.filesUnsupported === 0 ? (
                translate("SHAREPOINT/IMPORT_ALL_EXISTS")
              ) : (
                <>
                  {translate("SHAREPOINT/IMPORT_FILES_IMPORTED", {
                    count: syncResult.filesAdded.toString(),
                  })}
                  {syncResult.filesSkipped > 0 &&
                    `, ${translate("SHAREPOINT/IMPORT_SKIPPED", { count: syncResult.filesSkipped.toString() })}`}
                  {syncResult.filesUnsupported > 0 &&
                    `, ${translate("SHAREPOINT/IMPORT_UNSUPPORTED", { count: syncResult.filesUnsupported.toString() })}`}
                </>
              )}
            </div>
            {syncResult.errors.length > 0 && (
              <div className="mt-1.5 pl-6 text-xs text-red1">
                {syncResult.errors.map((e, i) => (
                  <div key={i}>{e}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Syncing progress */}
        {syncing && (
          <div className="mb-3 rounded-lg border border-yellow1/20 bg-yellow1/5 px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <IconLoader2 size={16} className="animate-spin text-yellow1" />
              <div className="flex-1">
                <span className="text-sm font-medium text-yellow1">
                  {translate("SHAREPOINT/IMPORTING_FILES")}
                </span>
                <p className="text-xs text-grey5">
                  {syncStatus && syncStatus.filesTotal > 0
                    ? translate("SHAREPOINT/IMPORT_PROGRESS", {
                        processed: syncStatus.filesProcessed.toString(),
                        total: syncStatus.filesTotal.toString(),
                      })
                    : translate("SHAREPOINT/DISCOVERING_FILES")}
                </p>
              </div>
            </div>
            {syncStatus && syncStatus.filesTotal > 0 && (
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-grey3">
                <div
                  className="h-full rounded-full bg-yellow1 transition-all duration-300"
                  style={{
                    width: `${Math.round((syncStatus.filesProcessed / syncStatus.filesTotal) * 100)}%`,
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Site selector */}
        <div className="mb-3">
          <select
            value={selectedSite?.id || ""}
            onChange={(e) => handleSiteSelect(e.target.value)}
            disabled={syncing}
            className="w-full rounded-lg border border-grey3 bg-grey2 px-3 py-2 text-sm text-white transition-colors focus:border-yellow1 focus:outline-hidden disabled:opacity-50"
          >
            <option value="">{translate("SHAREPOINT/SELECT_SITE")}</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>

        {/* Browser */}
        <div
          className={`min-h-0 flex-1 overflow-hidden ${syncing ? "pointer-events-none opacity-50" : ""}`}
        >
          <SharepointFolderBrowser
            selectedSite={selectedSite}
            items={currentItems}
            breadcrumbs={breadcrumbs}
            loading={loading}
            onFolderClick={handleFolderClick}
            onBreadcrumbClick={handleBreadcrumbClick}
            selectedItemIds={selectedItemIds}
            onItemToggle={handleItemToggle}
            importedItemNames={importedItemNames}
            isValidFile={isValidFileType}
          />
        </div>

        {/* Selection footer */}
        {selectedItems.size > 0 && !syncing && (
          <div className="mt-3">
            <div className="flex items-center gap-3 rounded-lg bg-grey2 px-4 py-3">
              <span className="flex-1 text-sm text-white">
                {translate("SHAREPOINT/ITEMS_SELECTED_COUNT", {
                  count: selectedItems.size,
                })}
                {isCountingFolders ? (
                  <span className="ml-1.5 flex items-center gap-1 text-xs text-grey5">
                    <IconLoader2 size={12} className="animate-spin" />
                    {translate("SHAREPOINT/COUNTING_FILES")}
                  </span>
                ) : totalEstimatedFiles > 0 ? (
                  <span className="ml-1.5 text-yellow1">
                    {translate("SHAREPOINT/ESTIMATED_FILE_COUNT", {
                      count: totalEstimatedFiles,
                    })}
                  </span>
                ) : null}
              </span>
              <button
                onClick={handleClearAll}
                className="text-sm text-grey5 transition-colors hover:text-white"
              >
                {translate("SHAREPOINT/CLEAR_SELECTION")}
              </button>
              <Button
                label={translate("SHAREPOINT/IMPORT_BUTTON")}
                title={translate("SHAREPOINT/IMPORT_BUTTON_TITLE")}
                onClick={handleImport}
                variantColor="yellow"
                variantSize="small"
                isDisabled={exceedsFileLimit || isCountingFolders}
              />
            </div>
            {!isCountingFolders && totalEstimatedFiles > 0 && (
              <div className="mt-1.5 px-1 text-[10px] text-grey5">
                {translate("SHAREPOINT/FILE_TYPES_NOTE")}
              </div>
            )}
            {exceedsFileLimit && (
              <div className="mt-2 rounded-lg bg-red1/10 px-3 py-2 text-sm text-red1">
                {translate("SHAREPOINT/MAX_FILES_ERROR", {
                  max: SHAREPOINT_CONFIG.MAX_SYNC_FILES.toLocaleString(),
                })}
              </div>
            )}
          </div>
        )}

        {/* Imported Files */}
        {sharepointImportedFiles.length > 0 && (
          <div className="mt-3 rounded-lg border border-yellow1/25 bg-yellow1/5">
            <div className="flex w-full items-center gap-2.5 px-3 py-2.5">
              <button
                onClick={() => setImportedFilesExpanded(!importedFilesExpanded)}
                className="flex flex-1 items-center gap-2.5 transition-colors hover:opacity-80"
              >
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-yellow1/20">
                  <IconCheck size={12} className="text-yellow1" />
                </div>
                <span className="text-sm font-medium text-white">
                  {translate("SHAREPOINT/IMPORTED_FILES", {
                    count: sharepointImportedFiles.length.toString(),
                  })}
                </span>
                {importedFilesExpanded ? (
                  <IconChevronDown size={14} className="ml-auto text-grey5" />
                ) : (
                  <IconChevronRight size={14} className="ml-auto text-grey5" />
                )}
              </button>
              {onUnlinkAll && (
                <button
                  onClick={onUnlinkAll}
                  className="shrink-0 rounded border border-red1/50 px-2 py-1 text-xs text-red1 transition-colors hover:bg-red1/10"
                >
                  {translate("SHAREPOINT/UNLINK_ALL_BUTTON")}
                </button>
              )}
            </div>

            {importedFilesExpanded && (
              <div className="border-t border-yellow1/15 px-3 py-1.5">
                {sharepointImportedFiles.map((file) => {
                  const ext = getFileExtension(file.fileName)
                  return (
                    <div
                      key={file.id}
                      className="flex items-center gap-2.5 py-1.5"
                    >
                      <IconFile size={14} className="shrink-0 text-yellow1" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs text-white">
                          {file.fileName}
                        </div>
                        {file.sharepointPath && (
                          <div className="truncate text-[10px] text-grey5">
                            {file.sharepointPath}
                          </div>
                        )}
                      </div>
                      {ext && (
                        <span className="shrink-0 rounded bg-yellow1/10 px-1.5 py-0.5 text-xs text-yellow1/70">
                          .{ext}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </ModalBase>
  )
}
